// Local document store for the email builder. Source-of-truth for
// in-progress edits, with undo/redo stacks and dirty tracking. Mirrors the
// conventions from v3's `usePendingEdits` but uses Zustand so the store can
// be consumed from anywhere in the editor tree without prop drilling.
//
// State flow:
//   loadFromServer(doc) →  baseline = document = cloned(doc), stacks cleared
//   updateNode/removeNode/... →  push baseline to undo, set document = cloned(next)
//   undo() →  redo.push(document), document = undo.pop()
//   redo() →  undo.push(document), document = redo.pop()
//   markSaved(serverDoc) →  baseline = cloned(serverDoc); isDirty derives false
//
// Dirty is computed as `document !== baseline` (reference identity — cheap
// because every edit replaces `document` with a fresh clone). No hashing.

import { create } from 'zustand';
import {
  type EmailDocument,
  type EmailNode,
  findNode,
  findParent,
} from '@nexmail/shared-util/email-builder';

const MAX_HISTORY = 50;

export interface NodePatch {
  props?: Record<string, unknown>;
  style?: Record<string, unknown>;
  meta?: Record<string, unknown>;
  name?: string;
}

export interface DocStoreState {
  document: EmailDocument | null;
  baseline: EmailDocument | null;
  undoStack: EmailDocument[];
  redoStack: EmailDocument[];
  isSaving: boolean;
  saveError: string | null;
  lastSavedAt: string | null;
  // Bumped only on whole-document replacements driven from outside the
  // editor surface (loadFromServer / applyProposal / undo / redo). Components
  // that hold their own internal copy of a node's contents — notably the
  // CanvasKitEditor, which seeds itself once from props.canvasJson — watch
  // this counter to know when to discard their internal state and reseed
  // from the new document. Granular mutations (updateNode, removeNode, etc.)
  // do NOT bump this, because those paths typically originate from the
  // component itself and would otherwise create a reseed loop on every edit.
  externalApplyCount: number;

  // Lifecycle
  loadFromServer: (doc: EmailDocument) => void;
  markSaved: (savedDoc: EmailDocument, docAtSaveStart?: EmailDocument) => void;
  setSaving: (saving: boolean) => void;
  setSaveError: (err: string | null) => void;
  // Replace the working document with an AI-proposed one while keeping the
  // server baseline unchanged. The result is a dirty edit the user can save
  // or undo — no server round-trip and no version bump.
  applyProposal: (proposed: EmailDocument) => void;

  // Mutations — each pushes the current document onto the undo stack and
  // replaces `document` with a cloned-and-patched copy.
  updateNode: (nodeId: string, patch: NodePatch) => void;
  insertSubtree: (parentId: string, index: number, subtree: EmailNode) => void;
  removeNode: (nodeId: string) => void;
  moveNode: (nodeId: string, newParentId: string, newIndex: number) => void;
  resizeColumns: (
    colAId: string,
    colAWidth: string,
    colBId: string,
    colBWidth: string,
  ) => void;
  setDiagnosticResolution: (nodeId: string, code: string, resolution: string) => void;
  clearDiagnosticResolution: (nodeId: string, code: string) => void;

  // History
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  isDirty: () => boolean;
}

// Helper: push a new state onto undo, cap history at MAX_HISTORY.
function pushUndo(stack: EmailDocument[], doc: EmailDocument): EmailDocument[] {
  const next = [...stack, doc];
  if (next.length > MAX_HISTORY) next.shift();
  return next;
}

// In-place mutation helpers. Callers are expected to pass a clone of the
// document so callers don't see partial mutations. Each returns the mutated
// document for convenient chaining.
function applyNodePatch(doc: EmailDocument, nodeId: string, patch: NodePatch): EmailDocument {
  const node = findNode(doc, nodeId) as Record<string, unknown> | null;
  if (!node) return doc;
  if (patch.props) node.props = { ...(node.props as object), ...patch.props };
  if (patch.style) node.style = { ...(node.style as object ?? {}), ...patch.style };
  if (patch.meta) node.meta = { ...(node.meta as object ?? {}), ...patch.meta };
  if (patch.name !== undefined) node.name = patch.name;
  return doc;
}

function applyRemove(doc: EmailDocument, nodeId: string): EmailDocument {
  const parent = findParent(doc, nodeId) as Record<string, unknown> | null;
  if (!parent) return doc;
  const children = parent.children as Array<{ id: string }>;
  const idx = children.findIndex(c => c.id === nodeId);
  if (idx >= 0) children.splice(idx, 1);
  return doc;
}

function applyInsert(
  doc: EmailDocument,
  parentId: string,
  index: number,
  subtree: EmailNode,
): EmailDocument {
  const parent = findNode(doc, parentId) as Record<string, unknown> | null;
  if (!parent) return doc;
  const children = (parent.children as EmailNode[] | undefined) ?? [];
  const clamped = Math.max(0, Math.min(index, children.length));
  children.splice(clamped, 0, subtree);
  parent.children = children;
  return doc;
}

function applyMove(
  doc: EmailDocument,
  nodeId: string,
  newParentId: string,
  newIndex: number,
): EmailDocument {
  const oldParent = findParent(doc, nodeId);
  if (!oldParent || !('children' in oldParent) || !Array.isArray(oldParent.children)) return doc;
  const oldChildren = oldParent.children as EmailNode[];
  const oldIdx = oldChildren.findIndex(c => c.id === nodeId);
  if (oldIdx < 0) return doc;
  const [detached] = oldChildren.splice(oldIdx, 1);
  return applyInsert(doc, newParentId, newIndex, detached);
}

function applyResizeColumns(
  doc: EmailDocument,
  colAId: string,
  colAWidth: string,
  colBId: string,
  colBWidth: string,
): EmailDocument {
  applyNodePatch(doc, colAId, { props: { width: colAWidth } });
  applyNodePatch(doc, colBId, { props: { width: colBWidth } });
  return doc;
}

type ResolutionMap = Record<string, Array<{ code: string; resolution: string }>>;
type DocWithResolutions = EmailDocument & { diagnosticResolutions?: ResolutionMap };

function applySetResolution(
  doc: EmailDocument,
  nodeId: string,
  code: string,
  resolution: string,
): EmailDocument {
  const d = doc as DocWithResolutions;
  const map: ResolutionMap = { ...(d.diagnosticResolutions ?? {}) };
  const existing = map[nodeId] ? map[nodeId].filter((e: { code: string }) => e.code !== code) : [];
  map[nodeId] = [...existing, { code, resolution }];
  d.diagnosticResolutions = map;
  return d;
}

function applyClearResolution(doc: EmailDocument, nodeId: string, code: string): EmailDocument {
  const d = doc as DocWithResolutions;
  const map: ResolutionMap = { ...(d.diagnosticResolutions ?? {}) };
  if (!map[nodeId]) return d;
  const filtered = map[nodeId].filter((e: { code: string }) => e.code !== code);
  if (filtered.length === 0) delete map[nodeId];
  else map[nodeId] = filtered;
  d.diagnosticResolutions = map;
  return d;
}

// Factory so tests can instantiate isolated stores. The default export is a
// singleton for the running app.
export function createDocStore() {
  return create<DocStoreState>((set, get) => ({
    document: null,
    baseline: null,
    undoStack: [],
    redoStack: [],
    isSaving: false,
    saveError: null,
    lastSavedAt: null,
    externalApplyCount: 0,

    loadFromServer: doc => {
      const cloned = structuredClone(doc);
      set({
        document: cloned,
        baseline: cloned,
        undoStack: [],
        redoStack: [],
        saveError: null,
        externalApplyCount: get().externalApplyCount + 1,
      });
    },

    markSaved: (savedDoc, docAtSaveStart) => {
      const cloned = structuredClone(savedDoc);
      const { document } = get();
      // When no edits happened during the save (current ref matches the ref
      // we saved), unify document + baseline to the same clone so isDirty
      // resolves false. Otherwise keep the in-progress document but sync
      // meta.version so the next save sends the correct expectedVersion.
      const userEditedDuringSave = docAtSaveStart !== undefined && document !== docAtSaveStart;
      if (!userEditedDuringSave) {
        set({
          document: cloned,
          baseline: cloned,
          lastSavedAt: new Date().toISOString(),
          saveError: null,
        });
      } else {
        const next = document
          ? { ...document, meta: { ...document.meta, version: cloned.meta.version } }
          : document;
        set({
          document: next,
          baseline: cloned,
          lastSavedAt: new Date().toISOString(),
          saveError: null,
        });
      }
    },

    setSaving: saving => set({ isSaving: saving }),
    setSaveError: err => set({ saveError: err }),

    applyProposal: proposed => {
      const { document, baseline, undoStack, externalApplyCount } = get();
      if (!document) return;
      // Normalize meta.version to the baseline's version so the next save
      // sends the correct expectedVersion — remix bumps versions server-side
      // on its no-op updates, but we treat the proposal as unsaved.
      const baselineVersion = baseline?.meta.version ?? document.meta.version;
      const cloned = structuredClone(proposed);
      cloned.meta = { ...cloned.meta, version: baselineVersion };
      set({
        document: cloned,
        undoStack: pushUndo(undoStack, document),
        redoStack: [],
        externalApplyCount: externalApplyCount + 1,
      });
    },

    updateNode: (nodeId, patch) => {
      const { document, undoStack } = get();
      if (!document) return;
      const next = applyNodePatch(structuredClone(document), nodeId, patch);
      set({
        document: next,
        undoStack: pushUndo(undoStack, document),
        redoStack: [],
      });
    },

    insertSubtree: (parentId, index, subtree) => {
      const { document, undoStack } = get();
      if (!document) return;
      const next = applyInsert(structuredClone(document), parentId, index, structuredClone(subtree));
      set({
        document: next,
        undoStack: pushUndo(undoStack, document),
        redoStack: [],
      });
    },

    removeNode: nodeId => {
      const { document, undoStack } = get();
      if (!document) return;
      const next = applyRemove(structuredClone(document), nodeId);
      set({
        document: next,
        undoStack: pushUndo(undoStack, document),
        redoStack: [],
      });
    },

    moveNode: (nodeId, newParentId, newIndex) => {
      const { document, undoStack } = get();
      if (!document) return;
      const next = applyMove(structuredClone(document), nodeId, newParentId, newIndex);
      set({
        document: next,
        undoStack: pushUndo(undoStack, document),
        redoStack: [],
      });
    },

    resizeColumns: (colAId, colAWidth, colBId, colBWidth) => {
      const { document, undoStack } = get();
      if (!document) return;
      const next = applyResizeColumns(
        structuredClone(document),
        colAId,
        colAWidth,
        colBId,
        colBWidth,
      );
      set({
        document: next,
        undoStack: pushUndo(undoStack, document),
        redoStack: [],
      });
    },

    setDiagnosticResolution: (nodeId, code, resolution) => {
      const { document, undoStack } = get();
      if (!document) return;
      const next = applySetResolution(structuredClone(document), nodeId, code, resolution);
      set({
        document: next,
        undoStack: pushUndo(undoStack, document),
        redoStack: [],
      });
    },

    clearDiagnosticResolution: (nodeId, code) => {
      const { document, undoStack } = get();
      if (!document) return;
      const next = applyClearResolution(structuredClone(document), nodeId, code);
      set({
        document: next,
        undoStack: pushUndo(undoStack, document),
        redoStack: [],
      });
    },

    undo: () => {
      const { document, undoStack, redoStack, externalApplyCount } = get();
      if (!document || undoStack.length === 0) return;
      const prev = undoStack[undoStack.length - 1];
      set({
        document: prev,
        undoStack: undoStack.slice(0, -1),
        redoStack: [...redoStack, document],
        externalApplyCount: externalApplyCount + 1,
      });
    },

    redo: () => {
      const { document, undoStack, redoStack, externalApplyCount } = get();
      if (!document || redoStack.length === 0) return;
      const next = redoStack[redoStack.length - 1];
      set({
        document: next,
        redoStack: redoStack.slice(0, -1),
        undoStack: pushUndo(undoStack, document),
        externalApplyCount: externalApplyCount + 1,
      });
    },

    canUndo: () => get().undoStack.length > 0,
    canRedo: () => get().redoStack.length > 0,
    isDirty: () => {
      const { document, baseline } = get();
      return !!document && document !== baseline;
    },
  }));
}

export const useDocStore = createDocStore();
